import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useRouter } from "next/router";
import { useState, useCallback, useEffect, useRef } from "react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@repo/ui/components/ui/form";
import { AuthFlowCard } from "../auth-flow-card";
import { Body } from "@repo/ui/components/ui/text";
import { Button } from "@repo/ui/components/ui/button";
import { motion } from "motion/react";
import { Input } from "@repo/ui/components/ui/input";
import { cn } from "@repo/common/utils/cn";
import { useSignupMutations } from "./hooks/use-signup-mutations";
import { useLogin } from "../login/hooks/use-login";
import { useCompanyUserExists } from "./hooks/use-company-user-exists";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@repo/ui/components/ui/input-group";
import { ChevronDown, Eye, EyeOff } from "lucide-react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Card, CardContent } from "@repo/ui/components/ui/card";
import { AUTH_FLOW_INPUT_CLASSNAME } from "../constants";
import { FormFooter } from "../form-footer";
const signupSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(10, "Password must be at least 10 characters")
});
export type SignupFormValues = z.infer<typeof signupSchema>;
const MARKETING_SITE_BASE_URL = "https://www.home.works";
export function Signup({
  onSignupSuccess
}: {
  onSignupSuccess: () => void;
}) {
  const router = useRouter();
  const [emailBlurred, setEmailBlurred] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const {
    executeRecaptcha
  } = useGoogleReCaptcha();
  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    mode: "onBlur",
    defaultValues: {
      email: "",
      password: ""
    }
  });
  const handleLogin = () => {
    router.push("/login");
  };
  const email = form.watch("email");
  const {
    data: userExists
  } = useCompanyUserExists(email, emailBlurred);
  const loginMutation = useLogin(() => onSignupSuccess());
  const signupMutation = useSignupMutations({
    onSuccess: () => {
      loginMutation.mutate({
        username: form.getValues("email"),
        password: form.getValues("password")
      });
    },
    onError: error => {
      console.error("error", error);
    }
  });

  // use refs to avoid including unstable dependencies in useCallback
  const formRef = useRef(form);
  const signupMutationRef = useRef(signupMutation);

  // keep refs up to date
  useEffect(() => {
    formRef.current = form;
    signupMutationRef.current = signupMutation;
  }, [form, signupMutation]);
  const handleCreateAccount = useCallback(async () => {
    if (!executeRecaptcha) {
      console.error("reCaptcha is not ready yet");
      formRef.current.setError("root", {
        type: "manual",
        message: "reCAPTCHA is not ready. Please refresh the page and try again."
      });
      return;
    }

    // retry logic for script initialization timing issues
    let retries = 3;
    let token: string | null = null;
    let lastError: Error | null = null;
    while (retries > 0 && !token) {
      try {
        // use "signup" action name to match backend expectations
        token = await executeRecaptcha("signup");
        break;
      } catch (error_0) {
        lastError = error_0 instanceof Error ? error_0 : new Error(String(error_0));
        const errorMessage = lastError.message;

        // if it's a "not loaded" error, wait and retry
        if (errorMessage.includes("not loaded in api.js") && retries > 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
          retries--;
          continue;
        }

        // for other errors or last retry, break and handle error
        break;
      }
    }
    if (!token) {
      // check if it's an invalid site key error
      const errorMessage_0 = lastError?.message || "";
      const isInvalidKeyError = errorMessage_0.includes("Invalid site key") || errorMessage_0.includes("not loaded in api.js");
      if (isInvalidKeyError) {
        console.error("reCaptcha error:", lastError);
        formRef.current.setError("root", {
          type: "manual",
          message: "reCAPTCHA configuration error. The site key may not be valid for this domain. Please contact support."
        });
        return;
      }
      console.error("reCaptcha token generation failed:", lastError);
      formRef.current.setError("root", {
        type: "manual",
        message: "Unable to generate reCAPTCHA token. Please try again."
      });
      return;
    }

    // validate token format
    if (typeof token !== "string" || token.trim().length === 0) {
      console.error("Invalid reCAPTCHA token format:", {
        type: typeof token,
        length: token?.length,
        isEmpty: !token || token.trim().length === 0
      });
      formRef.current.setError("root", {
        type: "manual",
        message: "Invalid reCAPTCHA token. Please try again."
      });
      return;
    }
    try {
      signupMutationRef.current.mutate({
        email: formRef.current.getValues("email"),
        password: formRef.current.getValues("password"),
        recaptchaToken: token
      });
    } catch (error_1) {
      console.error("Signup mutation error:", error_1);
      formRef.current.setError("root", {
        type: "manual",
        message: "Unable to create account. Please try again."
      });
    }
  }, [executeRecaptcha]);
  return <div className="w-full flex flex-col gap-4 items-center justify-start md:justify-center">
      <AuthFlowCard title="Create your account">
        <Form {...form}>
          <form onSubmit={e => {
          form.handleSubmit(handleCreateAccount)(e);
        }} className="space-y-4 mx-auto w-full">
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2,
            ease: "easeOut"
          }}>
              <FormField control={form.control} name={"email"} render={({
              field
            }) => <FormItem>
                    <FormLabel weight="medium">Email address</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="you@yourcompany.com" onBlur={() => {
                  field.onBlur();
                  setEmailBlurred(true);
                }} sizeVariant="lg" className={cn(form.formState.errors?.email ? "border-status-error" : "", AUTH_FLOW_INPUT_CLASSNAME)} />
                    </FormControl>
                    {userExists ? <FormMessage>
                        This email address is already in use.
                      </FormMessage> : null}
                    <FormMessage />
                  </FormItem>} />
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3,
            ease: "easeOut"
          }}>
              <FormField control={form.control} name={"password"} render={({
              field: field_0
            }) => <FormItem>
                    <FormLabel weight="medium">Password</FormLabel>
                    <FormControl>
                      <InputGroup className={cn("h-12", AUTH_FLOW_INPUT_CLASSNAME)}>
                        <InputGroupInput {...field_0} type={showPassword ? "text" : "password"} placeholder="Create your password" passwordrules="minlength:10" autocomplete="new-password" sizeVariant="lg" />
                        <InputGroupAddon align="inline-end" onClick={() => setShowPassword(!showPassword)} className="cursor-pointer">
                          {showPassword ? <EyeOff /> : <Eye />}
                        </InputGroupAddon>
                      </InputGroup>
                    </FormControl>
                    <FormMessage className={cn("text-foreground-muted", form.formState.errors?.["password"] ? "text-status-error-darken-1" : "")}>
                      {form.formState.errors?.["password"]?.message ? form.formState.errors?.["password"]?.message : "Password must be at least 10 characters"}
                    </FormMessage>
                  </FormItem>} />
            </motion.div>

            {form.formState.errors.root && <FormMessage className="text-status-error text-sm">
                {form.formState.errors.root.message}
              </FormMessage>}

            <Button type="submit" className={cn("w-full py-3.5 bg-brand hover:bg-brand-alt active:bg-brand-alt text-surface h-12")} disabled={!form.formState.isValid || !!userExists || !executeRecaptcha}>
              Create account
            </Button>

            <Body className="text-center pt-3 text-xs text-foreground-muted">
              By continuing, you agree to our{" "}
              <a href={`${MARKETING_SITE_BASE_URL}/terms-of-service`} className="underline hover:text-gray-600">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href={`${MARKETING_SITE_BASE_URL}/privacy-policy`} className="underline hover:text-gray-600">
                Privacy Policy
              </a>
            </Body>
          </form>
        </Form>
      </AuthFlowCard>

      <div className="flex flex-col gap-2 items-center justify-center pt-6">
        <div className="flex flex-col gap-4 items-center justify-center">
          <div className="flex items-center gap-1 cursor-pointer" onClick={() => setShowEmployeeForm(!showEmployeeForm)}>
            <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", showEmployeeForm ? "rotate-0" : "-rotate-90")} />
            <Body className="text-center">
              Need employee access to Homeworks?
            </Body>
          </div>
          <motion.div initial={false} animate={{
          height: showEmployeeForm ? "auto" : 0,
          opacity: showEmployeeForm ? 1 : 0
        }} transition={{
          duration: 0.3,
          ease: "easeOut"
        }} style={{
          overflow: "hidden"
        }}>
            <Card className="border-0 shadow-none bg-brand-white-alt">
              <CardContent className="pl-0">
                <div className="flex flex-col gap-4">
                  <p className="font-semibold">
                    Your employer needs to assign you a user seat. They can do
                    this in Homeworks by:
                  </p>
                  <ol className="list-decimal list-inside pl-2 text-foreground-muted space-y-1">
                    <li>{`Going to Team > Employees and adding or editing an employee`}</li>
                    <li>Assigning a user seat</li>
                    <li>Setting your email and password</li>
                    <li>Saving the changes</li>
                  </ol>
                  <p className="text-foreground-muted">
                    Once your login is set up, reopen Homeworks and sign in with
                    the credentials they provide.
                  </p>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        <FormFooter onClick={handleLogin} buttonText="Login" />
      </div>
    </div>;
}